import type {
  EffectRulesDef,
  EffectStackingDef,
  EffectTextDef,
  StatusEffectDef,
  ZoneDef,
  ZoneDefaultsDef,
} from './types';

/*
 * The C# initializers of shared-cs StatusEffectDefs.cs, so a draft without a field shows (and validates as) what the server's parser
 * fills in. Nested blocks merge over these like Newtonsoft (it reuses the default objects); lists start empty.
 */

export const effectStackingDefaults = (): Required<EffectStackingDef> => ({
  onRepeat: 'refresh',
  addH: 0,
  maxH: 0,
  max: 0,
  values: [],
});

export const effectTextDefaults = (): Required<EffectTextDef> => ({
  tape: '',
  causeText: '',
  endText: '',
  icon: '',
  stackNames: [],
});

export const effectResistDefaults = () => ({
  stat: 'nrv',
  ignoreAt: [] as number[],
  shortenPerPt: 0,
  shortenAbove: 5,
  minDurationMult: 1,
});

export const statusEffectDefaults = (id = ''): StatusEffectDef => ({
  id,
  name: '',
  family: '',
  polarity: 'buff',
  scope: 'player',
  appliesTo: 'everyone',
  modifiers: [],
  duration: { kind: 'timed', hours: 0 },
  stacking: effectStackingDefaults(),
  resist: null,
  triggers: [],
  text: effectTextDefaults(),
  hidden: false,
  derived: null,
  mirror: '',
});

/** A status effect as the server parses it: absent fields and nested blocks from the C# defaults (explicit null stays null). */
export function effectAsParsed(raw: StatusEffectDef): StatusEffectDef {
  const d = statusEffectDefaults();
  const present = Object.fromEntries(Object.entries(raw).filter(([, v]) => v !== undefined));
  const e: StatusEffectDef = { ...d, ...present };
  const merge = <T extends object>(def: T, v: T | null | undefined): T | null | undefined =>
    v == null
      ? v
      : { ...def, ...Object.fromEntries(Object.entries(v).filter(([, x]) => x !== undefined)) };
  return {
    ...e,
    duration: merge({ kind: 'timed', hours: 0 }, e.duration),
    stacking: merge(effectStackingDefaults(), e.stacking),
    resist: merge(effectResistDefaults(), e.resist),
    text: merge(effectTextDefaults(), e.text),
  };
}

export const zoneDefaultsDefaults = (): Required<ZoneDefaultsDef> => ({
  radiusM: 150,
  homeRadiusM: null,
  campRadiusM: null,
  staleMinutes: 45,
  homeStaleMinutes: 720,
  campStaleMinutes: 240,
});

export const effectRulesDefaults = (): EffectRulesDef => ({
  maxActive: 4,
  statCap: 2,
  keys: {},
  derivedFamilies: {},
  zoneDefaults: zoneDefaultsDefaults(),
  baseZoneEffects: { home: [], camp: [] },
});

/** The default new zone radius (`effectRules.zoneDefaults.radiusM`, 150 m) is used when `polar.radiusM` is 0. */
export const zoneDefaults = (id = ''): ZoneDef => ({
  id,
  name: '',
  kind: 'place',
  shape: 'polar',
  polar: { lat: 0, lng: 0, radiusM: 150 },
  grid: null,
  staleMinutes: 0,
  effects: [],
});

/** The Effects section for the section editor (merged into `teamsSectionDefaults`). */
export const effectsSectionDefaults = { effectRules: effectRulesDefaults } as const;
